import * as jobApi from '@/api/job';
import { SubmitJobParams, jobStateEnum } from '@/api/job';
import { getAntdApp } from '@/utils/antdApp';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { fileService } from './filesService'; // 新增：导入fileService
import pathbe from 'path-browserify-esm';
import { websocketService, WSMessageType } from './websocketService';
import i18n from '../../i18n';
// Job interface
export interface Job {
    id_job?: string
    // jobUid is the platform's own id, unique across clusters. Subscribe with it when
    // available: id_job repeats between clusters, so two jobs on two clusters would
    // otherwise collide and overwrite each other's status.
    job_uid?: string
    // cluster the job runs on. Empty/absent means the default (local) cluster.
    cluster?: string
    path: string
    job_name: string,
    state: jobStateEnum
    tag: string // Unique identifier for the job, can be a timestamp or UUID
    created_at: number
}

// Job store interface
interface JobState {
    jobs: Record<string, Job>
    // Actions
    submitJob: (params: SubmitJobParams) => Promise<boolean>
    // Active cluster for submissions and status subscriptions.
    activeCluster: string
    setActiveCluster: (clusterId: string) => void
    addJob: (job: Job) => void
    updateJob: (path: string, job: Partial<Job>) => void
    removeJob: (job: Job) => void
    clearAllJobs: () => void
    destroyWebSocket: () => void
}

export class JobService {
    private writeDebounceTimers: Record<string, NodeJS.Timeout> = {};
    // Directories kept watched briefly after a job ends. The platform pulls the results
    // after the state becomes terminal, so the writes that matter arrive a little later;
    // removing the watch on the terminal message would miss them and leave the file tree
    // showing the directory as it was before the pull.
    private pendingJobUnwatch: Record<string, number> = {};

    // Store reference
    private updateJob: Map<string, ((path: string, job: Partial<Job>) => void)> = new Map();

    public jobPathList: string[] = [];

    constructor() {
        // 注册作业状态消息处理器
        websocketService.on(WSMessageType.JOB_STATUS, this.handleJobStatus.bind(this));
    }

    private handleJobStatus(data: any) {
        if (data && data.job) {
            const job = data.job;
            console.log("[JobService] Received job update:", job);

            const jobDirectory = pathbe.dirname(job.path);

            if (jobDirectory !== fileService.baseHome) {
                if (job.state === jobStateEnum.RUNNING || job.state === jobStateEnum.PENDING) {
                    if (this.pendingJobUnwatch[jobDirectory]) {
                        clearTimeout(this.pendingJobUnwatch[jobDirectory]);
                        delete this.pendingJobUnwatch[jobDirectory];
                    }
                    if (!this.jobPathList.includes(jobDirectory)) {
                        this.jobPathList.push(jobDirectory)
                        // 通知fileService监听新的作业路径
                        fileService.listenToJobPath(this.jobPathList);
                    }
                } else {
                    if (!this.jobPathList.includes(jobDirectory)) {
                        this.jobPathList.push(jobDirectory);
                        fileService.listenToJobPath(this.jobPathList);
                    }
                    if (!this.pendingJobUnwatch[jobDirectory]) {
                        this.pendingJobUnwatch[jobDirectory] = window.setTimeout(() => {
                            delete this.pendingJobUnwatch[jobDirectory];
                            this.jobPathList = this.jobPathList.filter(p => p !== jobDirectory);
                            fileService.listenToJobPath(this.jobPathList);
                        }, 5 * 60 * 1000);
                    }
                }
            }

            if (this.updateJob) {
                Array.from(this.updateJob.values()).forEach(callback => {
                    callback(job.tag, job);
                });
            }
        }
    }

    public setUpdateJobCallback(key: string, callback: (tag: string, job: Partial<Job>) => void) {
        if (!this.updateJob) {
            this.updateJob = new Map();
        }
        this.updateJob.set(key, callback);
    }

    public removeUpdateJobCallback(key: string) {
        if (this.updateJob) {
            this.updateJob.delete(key);
        }
    }

    /**
     * Clean up resources and reset state when a user logs out
     */
    public destroy() {
        // Clear debounce timers
        Object.keys(this.writeDebounceTimers).forEach(key => {
            clearTimeout(this.writeDebounceTimers[key]);
        });
        this.writeDebounceTimers = {};

        // 清空作业路径列表并通知fileService
        this.jobPathList = [];
        fileService.listenToJobPath([]);

        // 取消订阅作业状态消息
        websocketService.off(WSMessageType.JOB_STATUS, this.handleJobStatus.bind(this));
    }

    public addJob(job: Job) {
        console.log("[JobService] Adding job:", job);
        websocketService.send({
            type: WSMessageType.JOB_SUBSCRIBE,
            payload: {
                action: 'add',
                job: job
            }
        });
    }

    public removeJob(job: Job) {
        console.log("[JobService] Removing job:", job);
        websocketService.send({
            type: WSMessageType.JOB_SUBSCRIBE,
            payload: {
                action: 'remove',
                job: job
            }
        });
    }
}

export const jobService = new JobService();

// Zustand store
export const useJobStore = create<JobState>()(
    persist(
        immer((set, _get) => ({
            jobs: {},
            activeCluster: '',
            setActiveCluster: (clusterId: string) => {
                set((state) => { state.activeCluster = clusterId; });
            },

            submitJob: async (params: SubmitJobParams) => {
                const user = localStorage.getItem('userName');
                const tag = user + "_" + dayjs().unix().toString(); // Use current timestamp as a unique tag
                try {
                    const res = await jobApi.submitJob({ ...params, comment: tag })
                    if (res?.ok) {
                        // The backend returns its own globally unique job id. Recording it here
                        // is what lets status updates be matched exactly instead of by tag:
                        // scheduler job ids repeat across clusters, and two clusters can be
                        // running a job with the same tag in the same second.

                        // add bias 5 seconds to created_at
                        // This is to avoid the job being created in the future due to network delay
                        const createdAt = Math.floor((dayjs().unix()) / 1000)
                        const submitted = res.data as unknown as {
                            jobUid?: string,
                            cluster?: string,
                            // The scheduler's id: it names the returned output file together
                            // with the cluster.
                            remoteJobId?: string,
                        } | undefined;
                        const cluster = submitted?.cluster || params.cluster || '';
                        set((state) => {
                            state.jobs[tag] = {
                                job_name: params.jobName,
                                path: params.path,
                                state: jobStateEnum.PENDING,
                                created_at: createdAt,
                                tag: tag,
                                job_uid: submitted?.jobUid,
                                id_job: submitted?.remoteJobId,
                                cluster: cluster
                            }
                        });
                        jobService.addJob({
                            job_name: params.jobName,
                            path: params.path,
                            state: jobStateEnum.PENDING,
                            created_at: createdAt,
                            tag: tag,
                            job_uid: submitted?.jobUid,
                            id_job: submitted?.remoteJobId,
                            cluster: cluster
                        })
                        return true;
                    } else {
                        getAntdApp().message.error(res?.message || i18n.t('job_service.submit_failed', { ns: 'common' }));
                        return false
                    }
                } catch (e) {
                    getAntdApp().message.error(i18n.t('job_service.submit_failed', { ns: 'common' }));
                    console.error(e)
                    return false
                }
            },

            addJob: (job: Job) => {
                console.log("Adding job:", job);
                set((state) => {
                    state.jobs[job.tag] = job;
                });
                jobService.addJob({
                    job_name: job.job_name,
                    path: job.path,
                    state: job.state,
                    created_at: job.created_at,
                    tag: job.tag
                })
            },

            updateJob: (tag: string, jobUpdate: Partial<Job>) => {
                set((state) => {
                    if (state.jobs[tag]) {
                        console.log("updating job:", tag, jobUpdate);
                        Object.assign(state.jobs[tag], jobUpdate);
                    } else {
                        state.jobs[tag] = jobUpdate as Job;
                    }
                });
            },

            // updateJobStatus: (tag: string, status: jobStateEnum, jobId?: string) => {
            //     set((state) => {
            //         const job = state.jobs[tag];
            //         if (job) {
            //             job.state = status;
            //             if (!jobId) {
            //                 job.id_job = jobId;
            //             }
            //         }
            //     });
            // },

            removeJob: (job: Job) => {
                set((state) => {
                    delete state.jobs[job.tag];
                });
                jobService.removeJob(job)
            },

            clearAllJobs: () => {
                set((state) => {
                    state.jobs = {};
                });
            },

            destroyWebSocket: () => {
                jobService.destroy();
            }
        })),
        {
            name: 'job-storage',
            partialize: (state) => ({ jobs: state.jobs }),
        }
    )
);

// Set up the callback to update jobs from WebSocket
jobService.setUpdateJobCallback("jobStore", useJobStore.getState().updateJob);

// Simplified hook that only manages cleanup
export const useJobWebSocket = () => {
    const destroyWebSocket = useJobStore(state => state.destroyWebSocket);

    useEffect(() => {
        return () => {
            // Cleanup when component unmounts
            destroyWebSocket();
        };
    }, [destroyWebSocket]);

    return { destroyWebSocket };
};
